import * as Sentry from '@sentry/react';
import { IFrameErrorSpreedly, SpreedlyError } from 'store/add-card/types';

type SpreedlyStage = 'initialize' | 'reload' | 'tokenize';

const SPREEDLY_FEATURE_TAG = 'add-card';

/**
 * Reports Spreedly card-tokenization failures to Sentry.
 *
 * Tokenization errors happen entirely inside the Spreedly iframe and never
 * reach our backend, so without this they are invisible in monitoring. See
 * SMB-929 for why we need client-side visibility into "invalid card" reports.
 */
export const logSpreedlyTokenizeErrors = (errors: Array<SpreedlyError> = []): void => {
  Sentry.captureMessage('Spreedly tokenization failed', {
    level: 'error',
    tags: { feature: SPREEDLY_FEATURE_TAG, spreedly_stage: 'tokenize' },
    extra: {
      errors: errors.map(({ attribute, key, message }) => ({ attribute, key, message })),
    },
  });
};

/**
 * Reports Spreedly iframe initialize/reload console errors to Sentry. These
 * indicate the hosted fields failed to load (blocked iframe, network, etc.).
 */
export const logSpreedlyIframeError = (
  stage: Exclude<SpreedlyStage, 'tokenize'>,
  error: IFrameErrorSpreedly | Error | string,
): void => {
  Sentry.captureMessage(`Spreedly iframe ${stage} error`, {
    level: 'error',
    tags: { feature: SPREEDLY_FEATURE_TAG, spreedly_stage: stage },
    extra: { error },
  });
};
